const FORBIDDEN_KEYS = new Set([
  'children', 'className', 'style', 'height', 'width', 'render', 'component',
  'jsx', 'map', 'geojson', 'media', 'form',
]);

function fail(path: string, message: string): never {
  throw new Error(`Invalid form flow descriptor: ${path} ${message}.`);
}

function object(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.getPrototypeOf(value) !== Object.prototype) {
    return fail(path, 'must be a plain object');
  }
  return value as Record<string, unknown>;
}

function exact(value: Record<string, unknown>, allowed: ReadonlySet<string>, path: string): void {
  Object.keys(value).forEach((key) => {
    if (FORBIDDEN_KEYS.has(key)) fail(`${path}.${key}`, 'is renderer-owned and forbidden');
    if (!allowed.has(key)) fail(`${path}.${key}`, 'is not part of the sealed contract');
  });
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string' || !value.trim()) return fail(path, 'must be non-empty plain text');
  return value;
}

function optionalText(value: unknown, path: string): void {
  if (value !== undefined) text(value, path);
}

function array(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) return fail(path, 'must be an array');
  return value;
}

function boolean(value: unknown, path: string): void {
  if (typeof value !== 'boolean') fail(path, 'must be boolean');
}

function scalar(value: unknown, path: string): void {
  if (value !== null && !['string', 'number', 'boolean'].includes(typeof value)) {
    fail(path, 'must be a scalar value or null');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) fail(path, 'must be finite');
}

function selectValue(value: unknown, path: string): void {
  if (typeof value !== 'string' && typeof value !== 'number') fail(path, 'must be text or a finite number');
  if (typeof value === 'number' && !Number.isFinite(value)) fail(path, 'must be finite');
}

function uniqueIds(items: readonly unknown[], path: string): Set<string> {
  const ids = new Set<string>();
  items.forEach((item, index) => {
    const id = text(object(item, `${path}[${index}]`).id, `${path}[${index}].id`);
    if (ids.has(id)) fail(`${path}[${index}].id`, 'must be unique');
    ids.add(id);
  });
  return ids;
}

function validateRowState(value: unknown, path: string): void {
  if (value === undefined) return;
  const state = object(value, path);
  exact(state, new Set(['status', 'message']), path);
  const statuses = ['unvalidated', 'valid', 'invalid', 'pending'];
  if (!statuses.includes(text(state.status, `${path}.status`))) {
    fail(`${path}.status`, `must be one of ${statuses.join(', ')}`);
  }
  optionalText(state.message, `${path}.message`);
  if (state.status === 'invalid' && state.message === undefined) fail(`${path}.message`, 'is required for invalid state');
}

function finiteConstraint(value: unknown, path: string): void {
  if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value))) {
    fail(path, 'must be a finite number');
  }
}

function validateTextEditor(editor: Record<string, unknown>, path: string): void {
  exact(editor, new Set(['kind', 'placeholder', 'maxLength']), path);
  optionalText(editor.placeholder, `${path}.placeholder`);
  if (editor.maxLength !== undefined
    && (!Number.isInteger(editor.maxLength) || Number(editor.maxLength) <= 0)) {
    fail(`${path}.maxLength`, 'must be a positive integer');
  }
}

function validateNumberEditor(editor: Record<string, unknown>, path: string): void {
  exact(editor, new Set(['kind', 'placeholder', 'min', 'max', 'step', 'precision']), path);
  optionalText(editor.placeholder, `${path}.placeholder`);
  for (const key of ['min', 'max', 'step'] as const) finiteConstraint(editor[key], `${path}.${key}`);
  if (typeof editor.step === 'number' && editor.step <= 0) fail(`${path}.step`, 'must be greater than zero');
  if (editor.precision !== undefined
    && (!Number.isInteger(editor.precision) || Number(editor.precision) < 0)) {
    fail(`${path}.precision`, 'must be a non-negative integer');
  }
  if (typeof editor.min === 'number' && typeof editor.max === 'number' && editor.min > editor.max) {
    fail(`${path}.min`, 'must not exceed max');
  }
}

function validateSelectEditor(editor: Record<string, unknown>, path: string): void {
  exact(editor, new Set(['kind', 'placeholder', 'allowClear', 'options']), path);
  optionalText(editor.placeholder, `${path}.placeholder`);
  if (editor.allowClear !== undefined) boolean(editor.allowClear, `${path}.allowClear`);
  const options = array(editor.options, `${path}.options`);
  uniqueIds(options, `${path}.options`);
  const values = new Set<string>();
  options.forEach((value, index) => {
    const optionPath = `${path}.options[${index}]`;
    const option = object(value, optionPath);
    exact(option, new Set(['id', 'label', 'value', 'disabled']), optionPath);
    text(option.label, `${optionPath}.label`);
    selectValue(option.value, `${optionPath}.value`);
    if (option.disabled !== undefined) boolean(option.disabled, `${optionPath}.disabled`);
    const identity = `${typeof option.value}:${String(option.value)}`;
    if (values.has(identity)) fail(`${optionPath}.value`, 'must be unique');
    values.add(identity);
  });
}

function validateEditor(value: unknown, path: string): Record<string, unknown> {
  const editor = object(value, path);
  switch (text(editor.kind, `${path}.kind`)) {
    case 'text': validateTextEditor(editor, path); break;
    case 'number': validateNumberEditor(editor, path); break;
    case 'select': validateSelectEditor(editor, path); break;
    case 'boolean': exact(editor, new Set(['kind']), path); break;
    default: fail(`${path}.kind`, 'must be one of text, number, select, boolean');
  }
  return editor;
}

function validateEditableCell(value: unknown, editor: Record<string, unknown>, path: string): void {
  if (editor.kind === 'text' && value !== null && typeof value !== 'string') {
    fail(path, 'must be text or null for its editor');
  }
  if (editor.kind === 'number' && value !== null
    && (typeof value !== 'number' || !Number.isFinite(value))) {
    fail(path, 'must be a finite number or null for its editor');
  }
  if (editor.kind === 'boolean' && typeof value !== 'boolean') {
    fail(path, 'must be boolean for its editor');
  }
  if (editor.kind === 'select' && value !== null) {
    const options = editor.options as readonly Record<string, unknown>[];
    if (!options.some((option) => option.value === value)) {
      fail(path, 'must match a declared select option or be null');
    }
  }
}

function validateColumns(
  value: unknown,
  path: string,
  editable: boolean,
): { ids: Set<string>; editors: ReadonlyMap<string, Record<string, unknown>> } {
  const columns = array(value, path);
  if (!columns.length) fail(path, 'must contain at least one column');
  const ids = uniqueIds(columns, path);
  const editors = new Map<string, Record<string, unknown>>();
  columns.forEach((value, index) => {
    const columnPath = `${path}[${index}]`;
    const column = object(value, columnPath);
    exact(column, new Set(['id', 'label', 'role', 'valueLabels', ...(editable ? ['editor', 'helper'] : [])]), columnPath);
    text(column.label, `${columnPath}.label`);
    if (editable) optionalText(column.helper, `${columnPath}.helper`);
    if (column.role !== undefined
      && !['label', 'numeric', 'status', 'source', 'metadata'].includes(text(column.role, `${columnPath}.role`))) {
      fail(`${columnPath}.role`, 'must be one of label, numeric, status, source, metadata');
    }
    if (column.valueLabels !== undefined) {
      const labels = object(column.valueLabels, `${columnPath}.valueLabels`);
      if (!Object.keys(labels).length) fail(`${columnPath}.valueLabels`, 'must not be empty');
      Object.entries(labels).forEach(([key, label]) => {
        if (!key.trim()) fail(`${columnPath}.valueLabels`, 'keys must be non-empty');
        text(label, `${columnPath}.valueLabels.${key}`);
      });
    }
    if (editable) editors.set(text(column.id, `${columnPath}.id`), validateEditor(column.editor, `${columnPath}.editor`));
  });
  return { ids, editors };
}

function validateRows(
  value: unknown,
  path: string,
  columnIds: ReadonlySet<string>,
  editors: ReadonlyMap<string, Record<string, unknown>>,
): void {
  const rows = array(value, path);
  uniqueIds(rows, path);
  rows.forEach((value, index) => {
    const rowPath = `${path}[${index}]`;
    const row = object(value, rowPath);
    exact(row, new Set(['id', 'cells', 'validation']), rowPath);
    const cells = object(row.cells, `${rowPath}.cells`);
    columnIds.forEach((columnId) => {
      if (!Object.prototype.hasOwnProperty.call(cells, columnId)) {
        fail(`${rowPath}.cells.${columnId}`, 'must provide every declared column');
      }
    });
    Object.entries(cells).forEach(([columnId, cell]) => {
      if (!columnIds.has(columnId)) fail(`${rowPath}.cells.${columnId}`, 'does not match a declared column');
      scalar(cell, `${rowPath}.cells.${columnId}`);
      const editor = editors.get(columnId);
      if (editor) validateEditableCell(cell, editor, `${rowPath}.cells.${columnId}`);
    });
    validateRowState(row.validation, `${rowPath}.validation`);
  });
}

export function validateFmFormFlowCollectionTable(
  columns: unknown,
  rows: unknown,
  controlPath: string,
  editable: boolean,
): void {
  const { ids, editors } = validateColumns(columns, `${controlPath}.columns`, editable);
  validateRows(rows, `${controlPath}.rows`, ids, editors);
}
